import { defineStore } from 'pinia';
import axios from 'axios';
import { SecureStorage } from '@/utils/storage';

const BaseURL = process.env.VUE_APP_API_BASE_URL;

export const useCardStore = defineStore('card', {
  state: () => ({
    publicCards: [],
    privateCards: [],
    lastFetchTime: { public: null, private: null },
    lastFetchedLang: { public: null, private: null },
    isLoading: false,
    error: null,
  }),

  actions: {
    _cacheKey(sectorType) {
      return `app_cards_${sectorType}`;
    },

    _loadFromCache(sectorType, lang) {
      const cached = SecureStorage.getItem(this._cacheKey(sectorType));
      if (cached && cached.lang === lang && Array.isArray(cached.data)) {
        if (sectorType === 'public') {
          this.publicCards = cached.data;
        } else {
          this.privateCards = cached.data;
        }
        this.lastFetchTime[sectorType] = cached.time;
        this.lastFetchedLang[sectorType] = lang;
        return true;
      }
      return false;
    },

    async fetchCards(sectorType, lang = 'en') {
      if (this.isLoading) return;

      const cardData = sectorType === 'public' ? this.publicCards : this.privateCards;
      const lastFetch = this.lastFetchTime[sectorType];
      const lastLang = this.lastFetchedLang[sectorType];

      if (cardData.length > 0 && lastLang === lang) {
        if (lastFetch) {
          const oneHour = 60 * 60 * 1000;
          if ((Date.now() - lastFetch) < oneHour) return;
        }
      }

      if (this._loadFromCache(sectorType, lang)) return;

      this.isLoading = true;
      this.error = null;

      try {
        const url = sectorType === 'public'
          ? `${BaseURL}/state_orders_public/`
          : `${BaseURL}/state_orders_private/`;

        const headers = { 'Accept-Language': lang };
        const response = await axios.get(url, { headers });
        const data = Array.isArray(response.data) ? response.data : [];

        if (sectorType === 'public') {
          this.publicCards = data;
          this.lastFetchTime = { ...this.lastFetchTime, public: Date.now() };
          this.lastFetchedLang = { ...this.lastFetchedLang, public: lang };
        } else {
          this.privateCards = data;
          this.lastFetchTime = { ...this.lastFetchTime, private: Date.now() };
          this.lastFetchedLang = { ...this.lastFetchedLang, private: lang };
        }

        SecureStorage.setItem(this._cacheKey(sectorType), {
          data,
          lang,
          time: Date.now(),
        }, SecureStorage.TTL.CARD);
      } catch {
        this.error = `Failed to load ${sectorType} sector data. Please try again later.`;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
